import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Search, Tag, Image as ImageIcon, Upload, Loader2 } from "lucide-react";
import CoverThumb from "@/components/CoverThumb";
import ConfirmDialog from "@/components/ConfirmDialog";

const EMPTY = { sku: "", name: "", category_id: "", author: "", isbn: "", description: "", min_stock_level: 5, store_scope: "all", store_ids: [] };
// SKU / Author / ISBN are only meaningful for these curated categories.
// Any other category (art, decor, merch, etc.) hides those fields entirely.
const CATS_WITH_METADATA = new Set(["paa_taraq", "actual_facts", "master_secrets", "classics", "scriptures"]);

// Large centered cover preview + upload used inside the add/edit dialog.
// When `product` exists (editing), uploads happen immediately. When it doesn't
// (creating), the picked File is handed back to the parent via `onPickNew` so
// the parent can upload it after the product is created.
function CoverEditor({ product, onChange, onPickNew, pendingFile }) {
  const [busy, setBusy] = useState(false);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const remoteSrc = product?.cover_path && token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${product.cover_path}?auth=${encodeURIComponent(token)}`
    : "";
  // Local preview URL for a not-yet-uploaded file (new product flow)
  const localSrc = useMemo(() => (pendingFile ? URL.createObjectURL(pendingFile) : ""), [pendingFile]);
  useEffect(() => () => { if (localSrc) URL.revokeObjectURL(localSrc); }, [localSrc]);
  const src = localSrc || remoteSrc;
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!product?.id) {
      // New product mode — hand the file back to the parent
      onPickNew && onPickNew(f);
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${product.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover updated");
      onChange && onChange(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="w-40 h-52 rounded-sm bg-secondary flex items-center justify-center overflow-hidden border border-border" data-testid="cover-editor-preview">
        {src ? (
          <img src={src} alt="" className="w-full h-full object-contain" />
        ) : (
          <ImageIcon className="w-8 h-8 text-muted-foreground" strokeWidth={1.5}/>
        )}
      </div>
      <label className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest border border-border rounded-sm cursor-pointer hover:bg-secondary/60 transition-colors" data-testid="cover-editor-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5"/>}
        {src ? "Change photo" : "Upload photo"}
        <input type="file" accept="image/*" className="hidden" onChange={pick} disabled={busy}/>
      </label>
      {!product?.id && pendingFile && (
        <div className="text-xs text-muted-foreground">Photo will upload after you save the book</div>
      )}
    </div>
  );
}

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  // Filters
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");

  // New-category dialog
  const [openCat, setOpenCat] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", code: "", description: "" });

  // Themed confirmations
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingCategory, setPendingCategory] = useState(null);

  const [pendingCover, setPendingCover] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const [p, c, s] = await Promise.all([api.get("/products"), api.get("/categories"), api.get("/stores")]);
      setProducts(p.data); setCategories(c.data); setStores((s.data || []).filter(x => x.active !== false));
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setPendingCover(null); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setPendingCover(null);
    setForm({
      sku: p.sku, name: p.name, category_id: p.category_id,
      author: p.author||"", isbn: p.isbn||"",
      description: p.description||"", min_stock_level: p.min_stock_level,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, min_stock_level: parseInt(form.min_stock_level || 5, 10) };
      if (editing) {
        // Store scope is only meaningful on creation — strip on update.
        delete payload.store_scope; delete payload.store_ids;
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        // Validate scope choice
        if (payload.store_scope === "some" && (!payload.store_ids || payload.store_ids.length === 0)) {
          return toast.error("Pick at least one store, or choose 'All stores'");
        }
        if (payload.store_scope !== "all" && payload.store_scope !== "some") {
          delete payload.store_scope; delete payload.store_ids;
        } else if (payload.store_scope === "all") {
          delete payload.store_ids;
        } else {
          // "some" — keep list, drop the scope keyword since backend only checks store_ids when not "all"
          delete payload.store_scope;
        }
        const { data: created } = await api.post("/products", payload);
        // Upload the picked cover (if any) now that the product exists
        if (pendingCover) {
          const fd = new FormData();
          fd.append("file", pendingCover);
          try {
            await api.post(`/products/${created.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          } catch (err) {
            toast.error("Book created, but cover upload failed");
          }
        }
        const seeded = created.seeded_store_ids?.length || 0;
        toast.success(seeded ? `Product added · stocked at ${seeded} store${seeded === 1 ? "" : "s"}` : "Product added");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); setPendingCover(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const askDeleteProduct = (p) => setPendingProduct(p);
  const doDeleteProduct = async () => {
    const p = pendingProduct;
    if (!p) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPendingProduct(null); }
  };

  const submitCategory = async (e) => {
    e.preventDefault();
    if (!newCat.name.trim()) return toast.error("Give the category a name");
    try {
      const code = (newCat.code || newCat.name).trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
      await api.post("/categories", { name: newCat.name.trim(), code, description: newCat.description });
      toast.success("Category created");
      setOpenCat(false); setNewCat({ name: "", code: "", description: "" });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteCategory = (id) => {
    const cat = categories.find(c => c.id === id);
    if (!cat) return;
    const productsInCat = products.filter(p => p.category_id === id).length;
    if (productsInCat > 0) {
      return toast.error(`"${cat.name}" still has ${productsInCat} title${productsInCat === 1 ? "" : "s"}. Reassign or delete them before deleting the category.`);
    }
    setPendingCategory(cat);
  };
  const doDeleteCategory = async () => {
    const cat = pendingCategory;
    if (!cat) return;
    try {
      await api.delete(`/categories/${cat.id}`);
      toast.success("Category deleted");
      setCatFilter("all");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPendingCategory(null); }
  };

  const catMap = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c.name])), [categories]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter(p => {
      if (catFilter !== "all" && p.category_id !== catFilter) return false;
      if (!q) return true;
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q) ||
        (p.author || "").toLowerCase().includes(q) ||
        (p.isbn || "").toLowerCase().includes(q)
      );
    });
  }, [products, search, catFilter]);

  return (
    <div className="space-y-8" data-testid="products-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Catalog</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Products</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={openCat} onOpenChange={setOpenCat}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-category-btn"><Tag className="w-4 h-4 mr-2"/>New category</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">New category</DialogTitle>
                <DialogDescription className="sr-only">Create a new book category.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submitCategory} className="space-y-3">
                <div><Label>Name</Label><Input value={newCat.name} onChange={e=>setNewCat({...newCat, name:e.target.value})} required data-testid="category-name"/></div>
                <div>
                  <Label>Code <span className="text-muted-foreground">(auto)</span></Label>
                  <Input value={newCat.code} onChange={e=>setNewCat({...newCat, code:e.target.value})} placeholder="auto from name" data-testid="category-code"/>
                </div>
                <div><Label>Description (optional)</Label><Input value={newCat.description} onChange={e=>setNewCat({...newCat, description:e.target.value})} data-testid="category-description"/></div>
                <DialogFooter><Button type="submit" data-testid="category-submit">Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); setPendingCover(null); }}}>
            <DialogTrigger asChild><Button onClick={openNew} data-testid="add-product-btn"><Plus className="w-4 h-4 mr-2"/>New product</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] flex flex-col p-0 gap-0">
              <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
                <DialogTitle className="font-display">{editing ? "Edit product" : "Add product"}</DialogTitle>
                <DialogDescription className="sr-only">Book metadata: SKU, title, category, author, ISBN.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
                <div className="overflow-y-auto px-6 pb-4 space-y-3">
                  <CoverEditor
                    product={editing}
                    pendingFile={pendingCover}
                    onPickNew={setPendingCover}
                    onChange={(updated) => {
                      setEditing(updated);
                      setProducts((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
                    }}
                  />
                  <div><Label>Title</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} required data-testid="product-name"/></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Category</Label>
                      <Select value={form.category_id} onValueChange={(v)=>setForm({...form, category_id:v})}>
                        <SelectTrigger data-testid="product-category"><SelectValue placeholder="Pick category"/></SelectTrigger>
                        <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Min stock</Label><Input type="number" value={form.min_stock_level} onChange={(e)=>setForm({...form, min_stock_level:e.target.value})}/></div>
                  </div>
                  {CATS_WITH_METADATA.has(categories.find(c => c.id === form.category_id)?.code) && (
                    <>
                      <div><Label>SKU <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={form.sku} onChange={(e)=>setForm({...form, sku:e.target.value})} placeholder="Auto-generated if blank" data-testid="product-sku"/></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Author</Label><Input value={form.author} onChange={(e)=>setForm({...form, author:e.target.value})}/></div>
                        <div><Label>ISBN</Label><Input value={form.isbn} onChange={(e)=>setForm({...form, isbn:e.target.value})}/></div>
                      </div>
                    </>
                  )}
                  {!editing && (
                    <div className="space-y-2 border-t border-border pt-3">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">Available at</Label>
                      <Select value={form.store_scope} onValueChange={(v)=>setForm({...form, store_scope:v, store_ids: v === "some" ? form.store_ids : []})}>
                        <SelectTrigger data-testid="product-store-scope"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All stores</SelectItem>
                          <SelectItem value="some">Specific stores…</SelectItem>
                          <SelectItem value="none">Don't stock yet</SelectItem>
                        </SelectContent>
                      </Select>
                      {form.store_scope === "some" && (
                        <div className="space-y-1 max-h-32 overflow-y-auto rounded-sm border border-border p-2" data-testid="product-store-picker">
                          {stores.map(s => {
                            const checked = form.store_ids.includes(s.id);
                            return (
                              <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-secondary/40 px-2 py-1 rounded-sm">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => setForm({
                                    ...form,
                                    store_ids: checked
                                      ? form.store_ids.filter(x => x !== s.id)
                                      : [...form.store_ids, s.id],
                                  })}
                                  data-testid={`product-store-${s.code}`}
                                />
                                <span className="font-mono text-xs text-muted-foreground w-16">{s.code}</span>
                                <span className="truncate">{s.name}</span>
                              </label>
                            );
                          })}
                          {stores.length === 0 && <div className="text-xs text-muted-foreground px-2 py-1">No active stores</div>}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        {form.store_scope === "all" && "Zero-quantity inventory rows will be added at every active store."}
                        {form.store_scope === "some" && `Selected ${form.store_ids.length} store${form.store_ids.length === 1 ? "" : "s"}.`}
                        {form.store_scope === "none" && "Rows are created lazily on first stock adjustment."}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter className="px-6 py-4 border-t border-border shrink-0 bg-background">
                  <Button type="submit" data-testid="product-submit">{editing ? "Save changes" : "Create"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5}/>
          <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search title, SKU, author, ISBN…" className="pl-9" data-testid="products-search"/>
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-[240px]" data-testid="products-category-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {catFilter !== "all" && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => deleteCategory(catFilter)}
            title="Delete this category"
            data-testid="delete-category-btn"
          >
            <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive"/>
          </Button>
        )}
        <div className="text-xs text-muted-foreground font-mono tabular-nums whitespace-nowrap" data-testid="products-count">
          {filtered.length} of {products.length}
        </div>
      </div>

      <div className="grid-border-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3 w-16"></th>
                <th className="text-left px-4 py-3">SKU</th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Author</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">ISBN</th>
                <th className="text-right px-4 py-3">Min</th>
                <th className="px-4 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && products.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`} className="animate-pulse" data-testid="product-row-skeleton">
                  <td className="px-4 py-3"><div className="h-9 w-9 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3"><div className="h-3 w-16 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3"><div className="h-3 w-56 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3"><div className="h-3 w-32 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3"><div className="h-3 w-24 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3"><div className="h-3 w-24 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3 text-right"><div className="h-3 w-6 bg-muted rounded-sm ml-auto"/></td>
                  <td className="px-4 py-3"/>
                </tr>
              ))}
              {filtered.map(p => (
                <tr key={p.id} className="group hover:bg-secondary/40 transition-colors">
                  <td className="px-4 py-3"><CoverThumb productId={p.id} coverPath={p.cover_path}/></td>
                  <td className="px-4 py-3 font-mono text-xs">{p.sku || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.author}</td>
                  <td className="px-4 py-3">{catMap[p.category_id]}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.isbn}</td>
                  <td className="px-4 py-3 text-right font-mono">{p.min_stock_level}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex gap-1">
                      <Button variant="ghost" size="icon" onClick={()=>openEdit(p)} data-testid={`edit-product-${p.sku}`} aria-label="Edit"><Pencil className="w-4 h-4"/></Button>
                      <Button variant="ghost" size="icon" onClick={()=>askDeleteProduct(p)} data-testid={`delete-product-${p.sku}`} aria-label="Delete"><Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive"/></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">No products match your filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingProduct}
        onOpenChange={(o)=>{ if(!o) setPendingProduct(null); }}
        title={`Delete "${pendingProduct?.name}"?`}
        description="This removes the product and every inventory row that references it across every store. This cannot be undone."
        confirmLabel="Delete product"
        destructive
        onConfirm={doDeleteProduct}
        testid="confirm-delete-product"
      />
      <ConfirmDialog
        open={!!pendingCategory}
        onOpenChange={(o)=>{ if(!o) setPendingCategory(null); }}
        title={`Delete category "${pendingCategory?.name}"?`}
        description="The category is empty, so nothing else will be removed."
        confirmLabel="Delete category"
        destructive
        onConfirm={doDeleteCategory}
        testid="confirm-delete-category"
      />
    </div>
  );
}
